import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Calendar, FileText, Printer, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/**
 * Componenta PaymentSchedule - Generează scadențar sumar și integral
 * 
 * Props:
 * - result: Obiectul rezultat din calculator (cu toate detaliile băncii)
 * - loanAmount: Suma creditului
 * - loanPeriodMonths: Perioada totală în luni
 * - fixedPeriodYears: Ani de dobândă fixă (0 pentru variabilă)
 * - fixedRate: Dobânda fixă (%)
 * - variableRate: Dobânda variabilă (%)
 * - lifeInsuranceRate: Rata asigurării de viață (% lunar pe sold)
 * - isOpen: Dacă modalul e deschis
 * - onClose: Handler pentru închidere
 */
const PaymentSchedule = ({
  result,
  loanAmount,
  loanPeriodMonths,
  fixedPeriodYears = 0,
  fixedRate,
  variableRate,
  lifeInsuranceRate = 0.026,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedYears, setExpandedYears] = useState(new Set());

  const [scheduleData, setScheduleData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [exporting, setExporting] = useState('');

  const requestBody = useMemo(() => ({
    loanAmount, loanPeriodMonths, fixedPeriodYears, fixedRate, variableRate,
    lifeInsuranceRate, bankName: result?.bank_name, productName: result?.product_name
  }), [loanAmount, loanPeriodMonths, fixedPeriodYears, fixedRate, variableRate, lifeInsuranceRate, result]);

  // Scadențarul vine din motorul unic de calcul (același ca documentul informativ / PDF)
  useEffect(() => {
    if (!isOpen || !loanAmount || !loanPeriodMonths) return;
    setLoadError('');
    axios.post(`${API_URL}/api/calculator/schedule`, requestBody, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(({ data }) => {
      const rows = data.rows.map(r => ({
        month: r.month, year: r.year, isFixed: r.is_fixed, rate: r.rate,
        startBalance: r.start_balance, interest: r.interest, principal: r.principal,
        insurance: r.insurance, payment: r.payment, totalPayment: r.total_payment, endBalance: r.end_balance
      }));
      setScheduleData({
        rows,
        yearlyData: data.yearly.map(y => ({
          year: y.year, isFixed: y.is_fixed, startBalance: y.start_balance, endBalance: y.end_balance,
          totalInterest: y.interest, totalPrincipal: y.principal, totalInsurance: y.insurance,
          totalPayment: y.payment, totalWithInsurance: y.total_payment,
          months: rows.filter(r => r.year === y.year)
        })),
        totals: {
          principal: data.totals.principal, interest: data.totals.interest, insurance: data.totals.insurance,
          withoutInsurance: data.totals.principal_interest, withInsurance: data.totals.principal_interest_insurance
        },
        checks: data.checks || []
      });
    }).catch(err => {
      console.error('Eroare scadențar:', err);
      setLoadError('Scadențarul nu a putut fi calculat. Datele introduse au fost păstrate – reîncercați.');
    });
  }, [isOpen, requestBody, loanAmount, loanPeriodMonths]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' RON';
  };

  const formatPercent = (value) => {
    return value?.toFixed(2) + '%';
  };

  const formatSmallPercent = (value) => {
    if (value == null) return '-';
    return String(parseFloat(value.toFixed(4))).replace('.', ',') + '%';
  };

  const toggleYearExpand = (year) => {
    const newSet = new Set(expandedYears);
    if (newSet.has(year)) {
      newSet.delete(year);
    } else {
      newSet.add(year);
    }
    setExpandedYears(newSet);
  };

  // Export PDF controlat de aplicație (fără antetele browserului / about:blank)
  const handlePrint = useCallback(async (mode) => {
    const pdfMode = mode === 'summary' ? 'summary' : 'full';
    setExporting(pdfMode);
    try {
      const response = await axios.post(`${API_URL}/api/calculator/schedule-pdf?mode=${pdfMode}`, requestBody, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Scadentar_${pdfMode === 'summary' ? 'sumar' : 'integral'}_${(result?.bank_name || 'credit').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Eroare export scadențar:', err);
      alert('Exportul PDF al scadențarului a eșuat. Datele rămân afișate – vă rugăm reîncercați.');
    } finally {
      setExporting('');
    }
  }, [requestBody, result]);

  if (!isOpen) return null;
  if (!scheduleData) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Scadențar</DialogTitle></DialogHeader>
          <p className={loadError ? 'text-red-600' : 'text-gray-600'} data-testid="schedule-loading">
            {loadError || 'Se calculează scadențarul...'}
          </p>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Scadentar {result?.bank_name || 'Bancă'} {result?.product_name && `- ${result?.product_name}`}
            </DialogTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePrint('summary')}
                disabled={!!exporting}
                data-testid="print-schedule-summary"
                className="flex items-center gap-1.5 text-xs"
              >
                <Printer className="h-3.5 w-3.5" />
                {exporting === 'summary' ? 'Se generează...' : 'PDF Sumar'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePrint('detailed')}
                disabled={!!exporting}
                data-testid="print-schedule-detailed"
                className="flex items-center gap-1.5 text-xs"
              >
                <Printer className="h-3.5 w-3.5" />
                {exporting === 'full' ? 'Se generează...' : 'PDF Integral'}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Rezumat */}
          <Card className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Total Principal</p>
                  <p className="font-semibold text-lg">{formatCurrency(scheduleData.totals.principal)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Dobândă</p>
                  <p className="font-semibold text-lg">{formatCurrency(scheduleData.totals.interest)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Asigurare</p>
                  <p className="font-semibold text-lg">{formatCurrency(scheduleData.totals.insurance)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Plată fără asig.</p>
                  <p className="font-semibold text-lg text-orange-600">{formatCurrency(scheduleData.totals.withoutInsurance)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Plată cu asig.</p>
                  <p className="font-semibold text-lg text-orange-600">{formatCurrency(scheduleData.totals.withInsurance)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs pentru Sumar / Integral */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="summary" data-testid="schedule-tab-summary">
                <FileText className="h-4 w-4 mr-2" />
                Scadențar Sumar
              </TabsTrigger>
              <TabsTrigger value="detailed" data-testid="schedule-tab-detailed">
                <Calendar className="h-4 w-4 mr-2" />
                Scadențar Integral
              </TabsTrigger>
            </TabsList>

            {/* Scadențar Sumar */}
            <TabsContent value="summary" className="flex-1 overflow-auto mt-0">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-24">An</TableHead>
                      <TableHead>Sold Inițial</TableHead>
                      <TableHead>Dobândă</TableHead>
                      <TableHead>Principal</TableHead>
                      <TableHead>Asigurare</TableHead>
                      <TableHead>Rata Totală</TableHead>
                      <TableHead>Rata + Asig.</TableHead>
                      <TableHead>Sold Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleData.yearlyData.map((yearData) => (
                      <React.Fragment key={yearData.year}>
                        <TableRow 
                          className={`cursor-pointer hover:bg-gray-50 ${yearData.isFixed ? 'bg-blue-50' : 'bg-gray-50'}`}
                          onClick={() => toggleYearExpand(yearData.year)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {expandedYears.has(yearData.year) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                              An {yearData.year}
                              {yearData.isFixed && (
                                <Badge variant="outline" className="text-xs bg-blue-100">Fix</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{formatCurrency(yearData.startBalance)}</TableCell>
                          <TableCell>{formatCurrency(yearData.totalInterest)}</TableCell>
                          <TableCell>{formatCurrency(yearData.totalPrincipal)}</TableCell>
                          <TableCell>{formatCurrency(yearData.totalInsurance)}</TableCell>
                          <TableCell>{formatCurrency(yearData.totalPayment)}</TableCell>
                          <TableCell className="font-semibold">{formatCurrency(yearData.totalWithInsurance)}</TableCell>
                          <TableCell>{formatCurrency(yearData.endBalance)}</TableCell>
                        </TableRow>
                        
                        {/* Luni expandate */}
                        {expandedYears.has(yearData.year) && yearData.months.map((row) => (
                          <TableRow key={row.month} className="bg-white text-sm">
                            <TableCell className="pl-10 text-gray-500">Luna {row.month}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.startBalance)}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.interest)}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.principal)}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.insurance)}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.payment)}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.totalPayment)}</TableCell>
                            <TableCell className="text-gray-600">{formatCurrency(row.endBalance)}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Scadențar Integral */}
            <TabsContent value="detailed" className="flex-1 overflow-auto mt-0">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-20">Luna</TableHead>
                      <TableHead>Dobândă %</TableHead>
                      <TableHead>Sold Inițial</TableHead>
                      <TableHead>Dobândă</TableHead>
                      <TableHead>Principal</TableHead>
                      <TableHead>Asigurare</TableHead>
                      <TableHead>Rata Totală</TableHead>
                      <TableHead>Rata + Asig.</TableHead>
                      <TableHead>Sold Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleData.rows.map((row) => (
                      <TableRow 
                        key={row.month}
                        className={row.isFixed ? 'bg-blue-50/50' : ''}
                      >
                        <TableCell className="font-medium">
                          {row.month}
                          {row.month === 1 && <Badge className="ml-1 text-xs">Start</Badge>}
                          {row.month === fixedPeriodYears * 12 && fixedPeriodYears > 0 && (
                            <Badge variant="outline" className="ml-1 text-xs">Sfârșit Fix</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={row.isFixed ? 'text-blue-600' : 'text-orange-600'}>
                            {formatPercent(row.rate)}
                          </span>
                        </TableCell>
                        <TableCell>{formatCurrency(row.startBalance)}</TableCell>
                        <TableCell>{formatCurrency(row.interest)}</TableCell>
                        <TableCell>{formatCurrency(row.principal)}</TableCell>
                        <TableCell>{formatCurrency(row.insurance)}</TableCell>
                        <TableCell>{formatCurrency(row.payment)}</TableCell>
                        <TableCell className="font-semibold">{formatCurrency(row.totalPayment)}</TableCell>
                        <TableCell>{formatCurrency(row.endBalance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>

          {scheduleData.checks.length > 0 && (
            <p className="text-red-600 text-sm mt-2" data-testid="schedule-checks">Verificări eșuate: {scheduleData.checks.join('; ')}</p>
          )}
          {/* Legendă */}
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-50 border rounded"></div>
              <span>Perioada cu dobândă fixă</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-white border rounded"></div>
              <span>Perioada cu dobândă variabilă</span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-gray-500">
                Asigurare viață: {formatSmallPercent(lifeInsuranceRate)} lunar din soldul curent
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentSchedule;
